import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Download, ExternalLink, Printer, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PdfPreviewModal — reusable in-page PDF preview that avoids browser redirect/popup-blocker issues.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - title: string
 *  - fileName: string (used for download)
 *  - fetcher: async () => axios response with `responseType: 'blob'`  (gives full control over auth headers, etc.)
 */
export default function PdfPreviewModal({ open, onClose, title = 'Preview', fileName = 'document.pdf', fetcher }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [blobUrl, setBlobUrl] = useState('');

  useEffect(() => {
    if (!open) return;
    let revoked = false;
    let createdUrl = '';
    (async () => {
      setLoading(true); setError(''); setBlobUrl('');
      try {
        const res = await fetcher();
        const blob = new Blob([res.data], { type: 'application/pdf' });
        createdUrl = URL.createObjectURL(blob);
        if (!revoked) setBlobUrl(createdUrl);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load PDF');
      } finally {
        if (!revoked) setLoading(false);
      }
    })();
    return () => {
      revoked = true;
      if (createdUrl) {
        // delay revoke to allow any in-flight printing/download
        setTimeout(() => URL.revokeObjectURL(createdUrl), 30000);
      }
    };
    // eslint-disable-next-line
  }, [open]);

  const openNewTab = () => {
    if (!blobUrl) return;
    const w = window.open(blobUrl, '_blank');
    if (!w) toast.error('Pop-up blocked — use Download instead');
  };
  const download = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl; a.download = fileName; a.click();
  };
  const printNow = () => {
    if (!blobUrl) return;
    const iframe = document.getElementById('pdf-preview-frame');
    try {
      iframe?.contentWindow?.focus();
      iframe?.contentWindow?.print();
    } catch {
      openNewTab();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0" data-testid="pdf-preview-modal">
        <DialogHeader className="px-4 py-2.5 border-b flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-base">{title}</DialogTitle>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" onClick={printNow} disabled={!blobUrl} data-testid="pdf-print">
              <Printer className="w-3.5 h-3.5 mr-1" /> Print
            </Button>
            <Button size="sm" variant="outline" onClick={download} disabled={!blobUrl} data-testid="pdf-download">
              <Download className="w-3.5 h-3.5 mr-1" /> Download
            </Button>
            <Button size="sm" variant="outline" onClick={openNewTab} disabled={!blobUrl} data-testid="pdf-open-tab">
              <ExternalLink className="w-3.5 h-3.5 mr-1" /> New Tab
            </Button>
            <Button size="sm" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
          </div>
        </DialogHeader>
        <div className="flex-1 bg-slate-100 overflow-hidden">
          {loading ? (
            <div className="h-full flex items-center justify-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Generating PDF…
            </div>
          ) : error ? (
            <div className="h-full flex items-center justify-center text-red-600 text-sm">{error}</div>
          ) : (
            <iframe
              id="pdf-preview-frame"
              src={blobUrl}
              title={title}
              className="w-full h-full border-0"
              data-testid="pdf-preview-frame"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
